import type { SkillTemplate } from './issue-log-xref.js';

export const sandboxTemplate: SkillTemplate = {
  name: 'sandbox',
  description: 'Orientation for Canary sandboxes — ephemeral app environments built from a verified template',
  version: '1.0.0',
  render: () => `---
name: sandbox
version: 1.0.0
description: Orientation for Canary sandboxes — ephemeral, containerized app environments built from a verified template. Use whenever the user mentions a sandbox or sandboxes (especially Canary sandboxes), sandbox templates or instances, provisioning / building / verifying / terminating a sandbox, sandbox artifacts / checklist / actions, or running PR / release tests against an ephemeral environment. Anchors to \`canary sandbox guide\`.
allowed-tools: Bash(canary sandbox:*), Bash(canary login:*), Read
---

# Canary Sandboxes

A **sandbox** is an ephemeral, containerized deployment of an app, provisioned on
demand from a verified **template**. Use it to stand up a real, browsable
environment for PR / release testing and to run server-side **actions** inside
workflow runs.

> **Start with the guide.** The authoritative, always-current reference is the
> built-in guide — read the relevant section before doing sandbox work:
>
> \`\`\`bash
> canary sandbox guide [goal|research|artifacts|actions|verify|checklist]
> canary sandbox --help
> \`\`\`
>
> This skill is a thin map; the guide is the source of truth. Don't re-derive it
> — hand off to it.

## Prerequisites

- \`canary\` CLI installed and authenticated (\`canary login\`).
- Run \`canary sandbox --help\` for the full command surface.

## Core concepts

- **Template** — the reusable, verified blueprint: a git repo + branch, its
  artifacts (Dockerfile, compose, scripts), a config schema, a setup checklist,
  and optional sandbox actions. Lifecycle: \`draft → authoring → verified\`.
- **Instance** — a running environment provisioned from a template, with a TTL
  (default 8h) and a public URL. Lifecycle:
  \`provisioning → running/healthy → terminated\` (or \`failed\`).

Templates are durable; instances are disposable. You author against a template,
then provision throwaway instances from it.

## Lifecycle & key verbs

\`\`\`
create     Create a template + its first authoring instance (from a property/repo)
            ↓  research the app, set config, upload artifacts   (guide: research, artifacts)
build      Build images, start containers, generate routing
            ↓  inspect with diagnostics / logs / run-command    (guide: verify)
verify     Provision a fresh instance, probe it, flip template → verified
            ↓
provision  Spin up fresh instances from the verified template (--ttl, --name)
terminate  Destroy one instance        delete  Remove the template + all instances
\`\`\`

See \`canary sandbox guide goal\` for the full authoring loop and \`guide verify\`
for the debugging playbook.

## Features (each defers to the guide for detail)

- **Checklist profiles** — \`minimal\` (code cloned), \`ci-ready\` (unit tests
  runnable), \`full-stack\` (app fully running). Items form a dependency-ordered
  list. → \`canary sandbox guide checklist\`
- **Artifacts** — the typed files that define the sandbox: \`dockerfile\`,
  \`docker-compose\`, \`setup-script\`, \`startup-script\`, \`seed-script\`, \`file\`.
  Uploads are template-level — rebuild an instance to apply.
  → \`canary sandbox guide artifacts\`
- **Config** — required / optional / sensitive env vars + per-template defaults,
  pushed to the instance as its \`.env\`. → \`canary sandbox config list|set\`
- **Multi-repo** — link several repos to one template as distinct services.
  → \`canary sandbox repo list|add\`
- **Actions** — template-scoped, parameterized scripts run against an instance,
  with typed input/output and optional credential output.
  → \`canary sandbox guide actions\`
- **Sizing** — instance presets \`small … 2xlarge\`. → \`canary sandbox set-size\`

## Getting started — create a sandbox

\`\`\`bash
canary sandbox create <property>          # template + first authoring instance
# read \`canary sandbox guide research\`, then:
canary sandbox config set <template-id> <KEY> <VALUE>
canary sandbox artifact create <template-id> <type> <local-file>
canary sandbox build <instance-id>        # apply artifacts/config
canary sandbox diagnostics <instance-id>  # + logs to confirm it's up
canary sandbox checklist complete <template-id> <item-id> --status success --summary "..."
canary sandbox verify <template-id>       # flip to verified
\`\`\`

Batch your artifact + config changes, then \`build\` **once**. Walk the guide
sections in order (\`goal → research → artifacts → verify → checklist\`) — they
contain the env-var classification, migration, and monorepo build-order details
that make the build succeed.

## Updating an existing sandbox

- Edit an artifact: \`canary sandbox artifact get <template-id> <type>\` → change
  it → \`canary sandbox artifact create <template-id> <type> <file>\` →
  \`canary sandbox build <instance-id>\` to apply (artifact uploads are
  template-level; running instances need a rebuild).
- Change config defaults: \`canary sandbox config set <template-id> <KEY> <VALUE>\`.
- Re-run / update the checklist, then \`canary sandbox verify <template-id>\` to
  re-verify after artifact changes.

## How sandboxes are used in Canary

1. **PR / release testing.** A verified template provisions an *ephemeral
   environment* that a test suite runs against. A spec/workflow binds to a
   template (the spec's \`sandboxTemplate:\` frontmatter); the suite
   auto-provisions one instance, shares it across the suite's runs, and tears it
   down afterward. → \`canary sandbox guide goal\`

2. **Actions inside workflows.** A sandbox action is a template-scoped script
   that runs *server-side* inside the run's backing instance (referenced by
   slug, so it's portable across environments). Its JSON output registers as
   workflow variables (\`$var.<key>\`), and it can emit an ephemeral login
   credential for downstream login steps. On a non-sandbox environment the step
   skips with a warning. → \`canary sandbox guide actions\`

## Cheat-sheet

\`\`\`bash
canary sandbox list                              # templates + instances
canary sandbox template <template-id>            # template detail + config
canary sandbox create <property>                 # new template + authoring instance
canary sandbox provision <template-id> --ttl 4   # fresh instance from a verified template
canary sandbox build <instance-id>               # build/start services
canary sandbox diagnostics <instance-id>         # container health, URL, phase
canary sandbox logs <instance-id> --service <name>
canary sandbox run-command <instance-id> "<cmd>" # host VM (add --service <name> for a container)
canary sandbox verify <template-id>              # provision + probe + mark verified
canary sandbox action list|get|create|execute <template-id> ...
canary sandbox checklist <template-id>           # setup progress
canary sandbox terminate <instance-id>           # destroy one instance
\`\`\`

## More info

- \`canary sandbox guide <section>\` and \`canary sandbox --help\` — the source of truth.
- Authoring deeper actions / credentials: \`canary sandbox guide actions\`.
`,
};
